import React from "react";
import { useIntl } from "react-intl";

// UI
import MaterialTable from "@material-table/core";
import _ from "lodash";

import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import { tableIcons } from "../ui-components/TableIcons";
import TableStyle from "./TableStyle";

const MAX_COLOR_NESTING = 4;

const OPTIONS = {
  filtering: true,
  pageSize: 10,
  searchFieldAlignment: "left",
  searchFieldStyle: {
    marginTop: "2px",
  },
  headerStyle: {
    fontWeight: "bold",
    fontSize: 15,
  },
  showTitle: false,
  toolbar: false,
  rowStyle: (rowData) => {
    const alpha =
      Math.min(MAX_COLOR_NESTING, (rowData.tableData?.path?.length ?? 1) - 1) /
      MAX_COLOR_NESTING;
    return {
      backgroundColor: `rgba(170, 170, 170, ${alpha})`,
    };
  },
};

const getLocalization = (intl) => ({
  header: {
    actions: "",
  },
  body: {
    editRow: {
      deleteText: intl.formatMessage({ id: "delete_element" }),
    },
  },
  pagination: {
    labelDisplayedRows: "{from}-{to} di {count}",
    labelRowsSelect: "",
  },
});

const DbModelTable = ({
  rows,
  setRows,
  push,
  columns,
  apiKey,
  staticTable,
  setSelectedParent,
  parentField,
  setData,
  editable,
  deletable,
  getCustomDelete,
  actions,
}) => {
  const intl = useIntl();
  const apiRequest = useCountryAxiosInstance();
  const _rows = rows && _.values(rows).map((user) => ({ ...user }));

  function manageUpdatedRow(oldData, newData) {
    setRows([...rows.filter((r) => r.id !== newData.id), newData]);
  }
  const getTreeIds = (curr) => {
    const children = curr.tableData?.childRows;
    if (!children) return [curr.id];
    return children.reduce(
      (prev, item) => [...prev, ...getTreeIds(item)],
      [curr.id]
    );
  };

  const _onRowUpdate = (newData, oldData) =>
    new Promise((resolve, reject) => {
      let isEqual = true;
      Object.entries(newData).forEach(([key, value]) => {
        if (oldData[key] !== value) {
          isEqual = false;
        }
      });

      const sanitizedNewData = Object.entries(newData).reduce(
        (prev, [field, value]) => ({
          ...prev,
          [field]:
            value === "null" &&
            columns.find((c) => c.type === "select" && c.field === field)
              ? null
              : value,
        }),
        {}
      );

      setImmediate(() => {
        if (isEqual) {
          resolve();
        } else {
          apiRequest
            .put(`/${apiKey}/${oldData.id}`, sanitizedNewData)
            .then((result) => {
              manageUpdatedRow(oldData, result);
              push({
                title: "Updated",
                type: "success",
              });
              resolve();
            })
            .catch((err) => {
              console.log(err);
              push({
                title: intl.formatMessage({ id: "server_error" }),
                type: "error",
              });
              reject();
            });
        }
      });
    });

  const _onRowDelete = (rowData) =>
    new Promise((resolve, reject) => {
      setImmediate(() => {
        const idsToDelete = getTreeIds(rowData);
        const promises = idsToDelete.map((id) =>
          apiRequest.delete(`/${apiKey}/${encodeURIComponent(id)}`)
        );
        Promise.all(promises)
          .then(() => {
            setRows(rows.filter((r) => !idsToDelete.includes(r.id)));
            push({
              title: "Deleted",
              type: "success",
            });
            resolve();
          })
          .catch(() => {
            push({
              title: intl.formatMessage({ id: "server_error" }),
              type: "error",
            });
            reject();
          });
      });
    });

  let customDelete;
  if (getCustomDelete) {
    customDelete = getCustomDelete(push, apiRequest, setData);
  }

  const onRowUpdate = staticTable ? null : editable ? _onRowUpdate : null;
  const onRowDelete = staticTable
    ? null
    : deletable
    ? customDelete || _onRowDelete
    : null;

  return (
    <TableStyle>
      <MaterialTable
        columns={columns.map((c) => ({
          ...c,
          type: c.tableType,
          render: c.render
            ? (rowData) => c.render(rowData, _rows, intl, c.lookup, setData)
            : undefined,
        }))}
        data={_rows}
        icons={tableIcons}
        isLoading={_rows === undefined}
        options={OPTIONS}
        localization={getLocalization(intl)}
        editable={{
          onRowUpdate,
          onRowDelete,
        }}
        parentChildData={(row, rows) =>
          parentField ? rows.find((a) => a.id === row[parentField]) : null
        }
        actions={[
          (rowData) => ({
            icon: tableIcons.Add,
            tooltip: "Create new child item",
            onClick: (event, rowData) => {
              setSelectedParent(rowData);
            },
            hidden: !(parentField in rowData),
          }),
        ].concat(actions || [])}
      />
    </TableStyle>
  );
};

export default React.memo(DbModelTable, (props, nextProps) => {
  // Render component only when rows changes
  return (
    _.isEqual(props.rows, nextProps.rows) &&
    _.isEqual(props.columns, nextProps.columns)
  );
});
